import React, { useState } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { useTranslation } from '@/contexts/TranslationContext';
import LoginForm from './LoginForm';
import Sidebar from './Sidebar';
import CustomerTable from './CustomerTable';
import CustomerForm from './CustomerForm';
import ReportForm from './ReportForm';
import ReportTableWithFilters from './ReportTableWithFilters';
import UserProfile from './UserProfile';
import TranslationEditor from './TranslationEditor';
import CalendarView from './CalendarView';
import SalesUsers from './SalesUsers';
import SettingsPage from './SettingsPage';
import { Customer, Report } from '@/types';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Menu, X, Plus } from 'lucide-react';


const AppLayout: React.FC = () => {
  const { currentUser, currentView, setCurrentView, logout, customers, saveCustomer, updateCustomer, deleteCustomer, reports, saveReport, deleteReport } = useAppContext();
  const { t } = useTranslation();
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | undefined>();
  const [editingReport, setEditingReport] = useState<Report | undefined>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  if (!currentUser) {
    return <LoginForm onLogin={() => {}} />;
  }

  const handleAddCustomer = () => {
    setEditingCustomer(undefined);
    setShowCustomerForm(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowCustomerForm(true);
  };

  const handleSaveCustomer = async (customerData: Omit<Customer, 'id'>) => {
    let success = false;
    if (editingCustomer) {
      success = await updateCustomer(editingCustomer.id, customerData);
    } else {
      success = await saveCustomer(customerData);
    }
    if (success) {
      setShowCustomerForm(false);
      setEditingCustomer(undefined);
    }
  };

  const handleAddReport = () => {
    setEditingReport(undefined);
    setShowReportForm(true);
  };

  const handleEditReport = (report: Report) => {
    setEditingReport(report);
    setShowReportForm(true);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'calendar':
        return <CalendarView reports={reports} customers={customers} />;
      case 'customers':
        return (
          <CustomerTable
            customers={customers}
            onEdit={handleEditCustomer}
            onDelete={deleteCustomer}
            onAdd={handleAddCustomer}
          />
        );
      case 'reports':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('reports')}</h2>
              <Button onClick={handleAddReport}>
                <Plus className="h-4 w-4 mr-2" />
                {t('addReport')}
              </Button>
            </div>
            <ReportTableWithFilters
              reports={reports}
              customers={customers}
              onEdit={handleEditReport}
              onDelete={deleteReport}
            />
          </div>
        );
      case 'users':
        return <SalesUsers />;
      case 'settings':
        return <SettingsPage />;
      case 'profile':
        return <UserProfile />;
      case 'translations':
        return <TranslationEditor />;
      default:
        return <CalendarView reports={reports} customers={customers} />;
    }

  };

  return (
    <div className="flex h-screen bg-gray-50">
      {isMobile && (
        <div className="fixed top-4 left-4 z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="bg-white shadow-md"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      )}
      
      <div className={`
        ${isMobile ? 'fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out' : 'relative'}
        ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
      `}>
        <Sidebar
          currentView={currentView}
          onViewChange={(view) => {
            setCurrentView(view);
            if (isMobile) setSidebarOpen(false);
          }}
          onLogout={logout}
        />
      </div>
      
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <main className={`flex-1 overflow-auto ${isMobile ? 'pt-16' : ''}`}>
        <div className="p-4 md:p-8">
          {renderContent()}
        </div>
      </main>
      
      <CustomerForm
        open={showCustomerForm}
        onClose={() => {
          setShowCustomerForm(false);
          setEditingCustomer(undefined);
        }}
        onSave={handleSaveCustomer}
        customer={editingCustomer}
      />
      
      <ReportForm
        open={showReportForm}
        onClose={() => {
          setShowReportForm(false);
          setEditingReport(undefined);
        }}
        report={editingReport}
      />
    </div>
  );
};

export default AppLayout;